import React, { useCallback, useMemo } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import Card from 'components/shared/Card';
import PatentMotion from '../PatentMotion';
import { TechnologyConflict } from 'store/types/Meeting';
import { ActivityType, IPCheckType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { defaultInputProps } from 'util/form';
import { meetingSessionToActivity } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

interface PatentedTechnologyCardProps {
  item: TechnologyConflict;
  index: number;
  updateItem: (id: string, patch: Partial<TechnologyConflict>) => void;
  removeItem: (id: string) => void;
  displayConflict: (conflict: TechnologyConflict) => void;
  clearConflict: () => void;
}

const PatentedTechnologyCard: React.FunctionComponent<PatentedTechnologyCardProps> = ({
  item,
  index,
  updateItem,
  removeItem,
  displayConflict,
  clearConflict,
}) => {
  const { meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();

  const itemNumber = index + 1;

  const enableSave = useMemo(
    () => item.isUsed === QuestionResponse.No || item.isJustified === QuestionResponse.No || item.hasLoa,
    [item.hasLoa, item.isJustified, item.isUsed]
  );
  const displayedItem = useMemo(
    () => ({
      ...item,
      itemNumber,
    }),
    [item, itemNumber]
  );

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        motion: meetingSession?.currentMotion,
        votingSession: meetingSession?.currentVotingSession,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          patentConflict: QuestionResponse.Yes,
        },
        technologyConflict: displayedItem,
      },
      'Saved Patented Technology'
    );
  }, [displayedItem, logActivity, meetingSession]);

  return (
    <Card
      title={<Typography variant="h6">Patented Technology Item #{itemNumber}</Typography>}
      bordered
      headerAction={
        <Button color={'error'} variant={'contained'} onClick={() => removeItem(item.id)}>
          Remove
        </Button>
      }
    >
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <GridContainer>
          <GridItem>
            <FormControl className="w-100">
              <FormLabel>Disclosed patented technology #{itemNumber}</FormLabel>
              <TextField
                label={'Brief description, e.g., patent title and number'}
                value={item.description}
                required
                multiline
                onChange={(e) => updateItem(item.id, { description: e.target.value })}
              ></TextField>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <DatePicker
                value={item.dateOfAssessment}
                onChange={(newValue) => updateItem(item.id, { dateOfAssessment: newValue })}
                label={'Date of Assessment'}
                renderInput={(params: TextFieldProps) => <TextField {...params} {...defaultInputProps} />}
              />
              <FormLabel>(If different from the date of Letter Ballot adjudication)</FormLabel>
            </FormControl>
          </GridItem>
          <GridItem className={commonStyles.buttonGroup}>
            <Button
              variant="contained"
              color="secondary"
              disabled={!item.description}
              onClick={() => displayConflict(displayedItem)}
            >
              Display Patent
            </Button>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>
                Is disclosed patented technology #{itemNumber} found to be "might be material" to the Standard/Safety
                Guideline?
              </FormLabel>
              <RadioGroup
                value={item.isUsed ?? ''}
                onChange={(event) => {
                  const value = event.target.value as QuestionResponse;
                  if (value === item.isUsed) return;

                  let patch: Partial<TechnologyConflict>;
                  if (value === QuestionResponse.No) {
                    patch = {
                      isUsed: value,
                      isJustified: undefined,
                      hasLoa: undefined,
                    };
                  } else {
                    patch = { isUsed: value };
                  }

                  const nextItem = { ...displayedItem, ...patch };
                  updateItem(item.id, patch);
                  void displayConflict(nextItem);
                }}
              >
                <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={'Yes (It is a PMPT)'} />
                <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
              </RadioGroup>
            </FormControl>
          </GridItem>
          {item.isUsed === QuestionResponse.No && (
            <GridItem>
              <Typography>No further action is needed for patented technology #{itemNumber}</Typography>
            </GridItem>
          )}
          {item.isUsed === QuestionResponse.Yes && (
            <GridItem>
              <FormControl>
                <FormLabel>Is the use of this PMPT technically justified?</FormLabel>
                <RadioGroup
                  value={item.isJustified ?? ''}
                  onChange={(event) => {
                    const value = event.target.value as QuestionResponse;
                    if (value === item.isJustified) return;

                    let patch: Partial<TechnologyConflict>;
                    if (value === QuestionResponse.No) {
                      patch = { isJustified: value, hasLoa: undefined };
                    } else {
                      patch = { isJustified: value };
                    }

                    const nextItem = { ...displayedItem, ...patch };
                    updateItem(item.id, patch);
                    void displayConflict(nextItem);
                  }}
                >
                  <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
                  <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
                </RadioGroup>
              </FormControl>
            </GridItem>
          )}
          {item.isJustified === QuestionResponse.No && (
            <GridItem>
              <Typography>The Document is failed and returned to the TF</Typography>
            </GridItem>
          )}
          {item.isJustified === QuestionResponse.Yes && (
            <GridItem>
              <FormControl>
                <FormLabel>Has an LOA for this patented technology been received from every owner?</FormLabel>
                <RadioGroup
                  value={item.hasLoa ?? ''}
                  onChange={(event) => {
                    const value = event.target.value as QuestionResponse;
                    if (value === item.hasLoa) return;

                    const patch = { hasLoa: value };
                    const nextItem = { ...displayedItem, ...patch };
                    updateItem(item.id, patch);
                    void displayConflict(nextItem);
                  }}
                >
                  <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
                  <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
                </RadioGroup>
              </FormControl>
            </GridItem>
          )}
          {item.hasLoa === QuestionResponse.Yes && (
            <GridItem>
              <Typography>
                Proceed to check Next one, or if this is the last one, Go To Assessment of disclosed copyrighted items.
              </Typography>
            </GridItem>
          )}
          {item.hasLoa === QuestionResponse.No && (
            <GridItem>
              <PatentMotion />
            </GridItem>
          )}
          <GridItem className={commonStyles.buttonGroup}>
            <Button variant="contained" color="secondary" disabled={!enableSave} onClick={handleSave}>
              Save
            </Button>
            <Button variant="contained" color="error" onClick={clearConflict}>
              Clear Patent
            </Button>
          </GridItem>
        </GridContainer>
      </LocalizationProvider>
    </Card>
  );
};

export default PatentedTechnologyCard;
